import { Skeleton, Table, type TablePaginationConfig, Spin } from 'antd';
import React, { memo, useState, useRef, useEffect, useCallback, createContext, useContext } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import SwitchComponent from '../SwitchComponent';
import styles from '../../style/components/datatable.module.scss';

const DragHandleContext = createContext<{
  attributes: any;
  listeners: any;
} | null>(null);

export type SortOrder = 'asc' | 'desc' | 'default';

export interface SortConfig {
  column: string;
  order: SortOrder;
}

export interface SortableColumn {
  column: string;
  onSort?: (sortConfig: SortConfig) => void;
}

interface DataTableProps {
  children: ColumnData[];
  data: Array<any>;
  setData?: (data: any) => void;
  rowKey?: string;
  pagination?: TablePaginationConfig;
  isDraggable?: boolean;
  onRowSelected?: (selectedRows: Array<object>) => void;
  loading?: boolean;
  actions?: ActionButtonProps[];
  onEdit?: string | ((row: any) => void);
  language?: 'tr' | 'en';
  onDelete?: (row: any) => void;
  deleteIcon?: string;
  deletePermission?: boolean;
  navigate?: any;
  emptyListComponent?: any;
  onDragEnd?: (event: DragEndEvent) => void;
  actionAlign?: 'center' | 'end' | 'start';
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  infiniteScrollThreshold?: number;
  isFetchingNextPage?: boolean;
  scrollHeight?: number;
  minHeight?: number;
  sortableColumns?: SortableColumn[];
  currentSort?: SortConfig;
  onSortChange?: (sortConfig: SortConfig) => void;
}

interface ColumnData {
  title: string | React.ReactNode;
  dataIndex?: string;
  key?: string;
  render?: any;
  width?: string;
  sorter?: any;
  statusMap?: any;
  date?: boolean;
  showTime?: boolean;
  onChange?: (value: any, row: any) => void;
  align?: any;
  onRowClick?: (row: any) => void;
  ellipsis?: boolean;
  visible?: boolean;
  sortable?: boolean;
  sortKey?: string;
}

interface ActionButtonProps {
  onClick?: (row: any) => void;
  link?: string;
  icon?: string;
  title?: string;
  permission?: boolean;
}

export const DragHandle: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dragHandleContext = useContext(DragHandleContext);

  if (!dragHandleContext) {
    return <>{children}</>;
  }

  return (
    <div
      {...dragHandleContext.attributes}
      {...dragHandleContext.listeners}
      style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center' }}
    >
      {children}
    </div>
  );
};

const SortableHeader: React.FC<{
  title: string | React.ReactNode;
  columnKey: string;
  currentSort?: SortConfig;
  onSort?: (sortConfig: SortConfig) => void;
  sortable?: boolean;
}> = ({ title, columnKey, currentSort, onSort, sortable = false }) => {
  const handleSort = () => {
    if (!sortable || !onSort) return;

    const currentOrder = currentSort?.column === columnKey ? currentSort.order : 'default';
    let nextOrder: SortOrder;

    switch (currentOrder) {
      case 'default':
        nextOrder = 'desc';
        break;
      case 'desc':
        nextOrder = 'asc';
        break;
      case 'asc':
        nextOrder = 'default';
        break;
      default:
        nextOrder = 'desc';
    }

    onSort({ column: columnKey, order: nextOrder });
  };

  const getSortIcon = () => {
    if (!sortable) {
      return null;
    }

    const isCurrentlySorted = currentSort?.column === columnKey;
    const isAsc = isCurrentlySorted && currentSort?.order === 'asc';
    const isDesc = isCurrentlySorted && currentSort?.order === 'desc';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1px',
        }}
      >
        <CaretUpOutlined
          style={{
            fontSize: '10px',
            color: isAsc ? '#52c41a' : '#d9d9d9',
            lineHeight: '1',
          }}
        />
        <CaretDownOutlined
          style={{
            fontSize: '10px',
            color: isDesc ? '#52c41a' : '#d9d9d9',
            lineHeight: '1',
          }}
        />
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        cursor: sortable ? 'pointer' : 'default',
        userSelect: 'none',
      }}
      onClick={handleSort}
    >
      <span>{title}</span>
      {getSortIcon()}
    </div>
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row = (props: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'default',
    ...(isDragging ? { position: 'relative', zIndex: 10 } : {}),
  };

  const dragHandleValue = {
    attributes,
    listeners,
  };

  return (
    <DragHandleContext.Provider value={dragHandleValue}>
      <tr
        {...props}
        ref={setNodeRef}
        style={style}
      />
    </DragHandleContext.Provider>
  );
};

const DataTable = (props: DataTableProps) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [internalSort, setInternalSort] = useState<SortConfig | undefined>(props.currentSort);
  const language = props.language || 'en';
  const scrollHeight = props.scrollHeight || 500;
  const minHeight = props.minHeight || 400;
  const tableBodyRef = useRef<HTMLDivElement | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync internal sort with external sort prop
  useEffect(() => {
    setInternalSort(props.currentSort);
  }, [props.currentSort]);

  const handleSortChange = (sortConfig: SortConfig) => {
    setInternalSort(sortConfig);
    if (props.onSortChange) {
      props.onSortChange(sortConfig);
    }
  };

  const { onLoadMore, loading, hasNextPage, infiniteScrollThreshold, isFetchingNextPage } = props;
  const debouncedHandleScroll = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (!onLoadMore || !tableBodyRef.current || loading || isLoadingMore || isFetchingNextPage) return;

      if (hasNextPage === false) return;

      const threshold = infiniteScrollThreshold || 100;
      const { scrollTop, scrollHeight, clientHeight } = tableBodyRef.current;

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        setIsLoadingMore(true);
        onLoadMore();
      }
    }, 150);
  }, [onLoadMore, loading, hasNextPage, infiniteScrollThreshold, isLoadingMore, isFetchingNextPage]);

  useEffect(() => {
    if (!props.onLoadMore) return;

    const findScrollContainer = () => {
      const tableWrapper = document.querySelector('.ant-table-body');
      if (tableWrapper) {
        tableBodyRef.current = tableWrapper as HTMLDivElement;
        return tableWrapper;
      }
      return null;
    };

    const timer = setTimeout(() => {
      const scrollContainer = findScrollContainer();
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', debouncedHandleScroll);
      }
    }, 100);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      clearTimeout(timer);
      if (tableBodyRef.current) {
        tableBodyRef.current.removeEventListener('scroll', debouncedHandleScroll);
      }
    };
  }, [props.onLoadMore, debouncedHandleScroll]);

  useEffect(() => {
    if (!props.loading && !props.isFetchingNextPage) {
      setIsLoadingMore(false);
    }
  }, [props.loading, props.isFetchingNextPage]);

  useEffect(() => {
    if (props.hasNextPage === false) {
      setIsLoadingMore(false);
    }
  }, [props.hasNextPage]);

  // Additional effect to reset loading state when isFetchingNextPage changes
  useEffect(() => {
    if (!props.isFetchingNextPage) {
      setIsLoadingMore(false);
    }
  }, [props.isFetchingNextPage]);

  const onSelectChange = (newSelectedRowKeys: any[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    if (props.onRowSelected) props.onRowSelected(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = props.children
    .filter((row) => row.visible !== false)
    .map((row, i) => {
      row.dataIndex = row.key;
      row.ellipsis = row.ellipsis ?? false;

      // Add sortable header if column is sortable
      if (row.sortable) {
        const sortKey = row.sortKey || row.key || '';

        if (typeof row.title === 'string') {
          row.title = (
            <SortableHeader
              title={row.title}
              columnKey={sortKey}
              currentSort={internalSort}
              onSort={handleSortChange}
              sortable={row.sortable}
            />
          );
        }
      }

      if (row.date) {
        row.render = (date: string) => {
          if (!date) return '-';
          const dateObj = new Date(date);
          return row.showTime ? dateObj.toLocaleString() : dateObj.toLocaleDateString();
        };
      } else if (row.key === 'visibilty') {
        row.render = (visibilty: boolean, currentRow: any) => (
          <SwitchComponent
            checked={visibilty}
            onChange={(checked: boolean) => row.onChange && row.onChange(checked, currentRow)}
          />
        );
        return row;
      } else if (row.key === 'priority') {
        row.render = () => <span>{1 + i++}</span>;
        return row;
      } else if (row.ellipsis && !row.render) {
        // Add ellipsis rendering for text content
        row.render = (text: any) => {
          if (text === null || text === undefined) return '-';
          const textStr = String(text);
          return (
            <div
              className="ellipsis-cell"
              style={{
                maxWidth: row.width || '200px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={textStr}
            >
              {textStr}
            </div>
          );
        };
      }

      return row;
    });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id && props.setData) {
      props.setData((prev: any[]) => {
        const activeIndex = prev.findIndex((i) => i.id === active.id);
        const overIndex = prev.findIndex((i) => i.id === over?.id);
        const newOrder = arrayMove(prev, activeIndex, overIndex);
        return newOrder;
      });
    }
  };

  const SkeletonTable = () => (
    <div
      style={{
        width: '100%',
        padding: '16px',
        border: '1px solid #f0f0f0',
        borderRadius: '8px',
        backgroundColor: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '12px 0',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {columns.map((_, index) => (
          <Skeleton
            key={index}
            active
            paragraph={{
              rows: 1,
              width: index === 0 ? '60px' : index === columns.length - 1 ? '40px' : '120px',
            }}
            title={false}
            avatar={false}
            style={{
              flex: 1,
              marginRight: index < columns.length - 1 ? '16px' : '0',
            }}
          />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
            padding: '8px 0',
          }}
        >
          {columns.map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              active
              paragraph={{
                rows: 1,
                width: colIndex === 0 ? '60px' : colIndex === columns.length - 1 ? '40px' : '120px',
              }}
              title={false}
              avatar={false}
              style={{
                flex: 1,
                marginRight: colIndex < columns.length - 1 ? '16px' : '0',
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div
      style={{ position: 'relative', minHeight: `${minHeight}px` }}
      className={`table-responsive datanew custom-table ${styles.dataTableContainer} ${props.loading ? 'loading' : ''} ${
        props.emptyListComponent && props.data.length === 0 && !props.loading ? 'empty-list-component' : ''
      }`}
    >
      {props.loading ? (
        <SkeletonTable />
      ) : !props.isDraggable ? (
        <Table
          className="table datanew dataTable"
          rowKey={props.rowKey || 'id'}
          rowSelection={props.onRowSelected && rowSelection}
          dataSource={props.data}
          columns={columns}
          pagination={false}
          scroll={{ y: scrollHeight, scrollToFirstRowOnChange: false }}
          tableLayout="fixed"
        />
      ) : (
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={(event) => {
            onDragEnd(event);
            if (props.onDragEnd) props.onDragEnd(event);
          }}
        >
          <SortableContext
            items={props.data.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              components={{
                body: {
                  row: Row,
                },
              }}
              className="table datanew dataTable"
              rowKey={props.rowKey || 'id'}
              rowSelection={props.onRowSelected && rowSelection}
              dataSource={props.data}
              columns={columns}
              pagination={false}
              scroll={{ y: scrollHeight, scrollToFirstRowOnChange: false }}
              tableLayout="fixed"
            />
          </SortableContext>
        </DndContext>
      )}

      {(isLoadingMore || isFetchingNextPage) && (
        <div
          style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            padding: '8px 12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Spin size="small" />
          <span style={{ fontSize: '12px', color: '#666' }}>{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</span>
        </div>
      )}
    </div>
  );
};

export default memo(DataTable);

